import { useCallback } from 'react';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

/**
 * Pill toggle (reference: Fallback Model switch).
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  'data-testid': dataTestId,
}: ToggleProps) {
  const handleToggle = useCallback(() => {
    if (!disabled) {
      onChange(!checked);
    }
  }, [checked, disabled, onChange]);

  return (
    <div data-testid={dataTestId ?? 'design-system-toggle'} className="flex items-start justify-between gap-4">
      <div>
        {label ? <p className="text-sm font-medium text-primary">{label}</p> : null}
        {description ? <p className="mt-1 text-xs text-muted">{description}</p> : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        data-testid={`${dataTestId ?? 'design-system-toggle'}-control`}
        onClick={handleToggle}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--kc-focus-ring)] ${
          checked ? 'bg-success' : 'bg-subtle'
        } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-surface shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
